import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import FAQSection from '../components/FAQSection';
import { Helmet } from "react-helmet";

const DigitalMarketing = () => {
    useEffect(() => {
        document.title = "MM Auto Tech | Business Support Solutions";
        const meta = document.querySelector("meta[name='description']") || document.createElement("meta");
        meta.name = "description";
        meta.content = "MM Auto Tech offers complete B2B support to automotive businesses. Simplify operations, improve customer service, and grow revenue.";
        if (!document.head.contains(meta)) document.head.appendChild(meta);
    }, []);
    const DigitalMarketingFAQs = [{
        q: 'How is your digital marketing service tailored for the automotive industry?',
        a: 'We only work with garages, auto repair shops, and service centers—so our strategies are based on deep industry insights and tested conversion models for bookings and lead generation.'
    },
    {
        q: 'What if I don’t have a website yet?',
        a: 'No problem. We can build a fast, mobile-friendly, and SEO-optimized website for your garage from scratch—aligned with your branding and business goals.'
    },
    {
        q: 'What kind of results can I expect?',
        a: 'Most of our clients start seeing measurable improvements in local search rankings, leads, and website traffic within the first 30-60 days, with scalable growth over time.'
    },
    {
        q: 'Is the consultation really free?',
        a: 'Yes! Our free strategy session includes a performance audit and a personalized roadmap—no obligation, no hidden fees.'
    },
    {
        q: 'Do you also manage ads and social media?',
        a: 'Absolutely. We manage Google Ads (PPC), Facebook/Instagram campaigns, and maintain your social presence—so you don’t have to worry about daily posting or ad optimization.'
    }
    ];

    return (
        <><Helmet>
            <title>Digital Marketing for Garages | MM AutoTech UK</title>
            <meta
                name="description"
                content="MM AutoTech provides digital marketing solutions for UK garages, including SEO, social media, and Google Ads management." />
            <link rel="canonical" href="https://mmautotech.com/services/digital-marketing" />
        </Helmet><div className="max-w-7xl mx-auto space-y-24">
                {/* Hero Section */}
                <section className="flex flex-col lg:flex-row items-stretch">
                    {/* Left Content */}
                    <div className="lg:w-1/2 p-8 flex flex-col justify-center">
                        <h1 className="text-4xl font-bold mb-6 text-blue-900">Digital Marketing for Auto Garages</h1>
                        <p className="text-gray-700 mb-6">
                            At MM AutoTech, we help garages and automotive businesses grow with data-driven digital marketing. From SEO to paid ads, our services are tailored to attract customers, increase revenue, and simplify your online presence.
                        </p>
                        <Link
                            to="/book-appointment"
                            className="inline-flex items-center justify-center text-sm font-semibold tracking-widest text-blue-900 border border-blue-900 px-6 py-2 rounded hover:bg-yellow-400 hover:border-yellow-400 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-900/30"
                            aria-label="Book an appointment"
                        >
                            Book Free Consultation
                        </Link>
                    </div>
                    <div
                        className="lg:w-1/2 h-64 lg:h-auto relative overflow-hidden"
                        style={{ clipPath: "polygon(15% 0, 100% 0, 100% 100%, 0% 100%)" }} // clip the container
                    >
                        <img
                            src="https://t4.ftcdn.net/jpg/04/93/22/19/360_F_493221997_700SRDbJGDKrEtxtnETDIRdcIHF9NUs4.jpg"
                            alt="Digital Marketing"
                            className="w-full h-72 md:h-full rounded shadow-lg object-cover" />
                    </div>
                </section>

                {/* Digital Marketing Services Overview */}
                <section className="bg-white text-gray-900 py-20 px-6 text-center">
                    <div className="max-w-4xl mx-auto mb-12">
                        <h2 className="text-4xl font-bold text-blue-900 mb-4">Digital Marketing Solutions for Garage Growth</h2>
                        <p className="text-gray-600 text-base md:text-lg">
                            We specialize in results-driven digital marketing tailored for the automotive aftermarket. From lead generation to CRM pipelines, our solutions help garages attract more customers, retain them longer, and boost revenue.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
                        {[
                            {
                                title: 'Search Engine Optimization (SEO)',
                                desc: 'Get found by customers actively searching for services you offer. Local and on-page SEO optimized for garages and service centers.',
                                icon: 'https://cdn-icons-png.flaticon.com/512/12597/12597796.png'
                            },
                            {
                                title: 'Google Ads (PPC)',
                                desc: 'Launch high-converting campaigns with measurable ROI. Perfect for promoting seasonal offers or specific services.',
                                icon: 'https://cdn-icons-png.flaticon.com/512/888/888961.png'
                            },
                            {
                                title: 'Social Media Management',
                                desc: 'Maintain a professional, engaging presence across Facebook, Instagram & WhatsApp to build trust and referrals.',
                                icon: 'https://cdn-icons-png.flaticon.com/512/2172/2172259.png'
                            },
                            {
                                title: 'Website & Landing Page Design',
                                desc: 'We build conversion-focused sites that reflect your garage’s brand and guide visitors into appointments.',
                                icon: 'https://cdn-icons-png.flaticon.com/512/8654/8654341.png'
                            },
                            {
                                title: 'Reputation & Review Management',
                                desc: 'Monitor and respond to online reviews, improve your star rating, and build local trust.',
                                icon: 'https://cdn-icons-png.flaticon.com/512/2867/2867263.png'
                            },
                            {
                                title: 'CRM & Lead Automation',
                                desc: 'We integrate smart tools to capture leads and automate responses, follow-ups, and client bookings.',
                                icon: 'https://cdn-icons-png.flaticon.com/512/8759/8759045.png'
                            }
                        ].map((item, index) => (
                            <div key={index} className="bg-white border shadow hover:shadow-xl transition p-6 rounded-lg text-left">
                                <img src={item.icon} alt={item.title} className="w-12 h-12 mb-4" />
                                <h3 className="text-lg font-semibold text-blue-800 mb-2">{item.title}</h3>
                                <p className="text-gray-600 text-sm">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* SEO Audit CTA */}
                <section className="py-20 px-6 bg-[#f8fafc] border-t border-b">
                    <div className="max-w-7xl mx-auto grid md:grid-cols-2 items-center gap-10">
                        <img src="https://smenews.digital/wp-content/uploads/2022/03/SEO-Techniques.jpg" alt="SEO Audit" className="rounded-lg shadow-lg w-full object-cover" />

                        <div>
                            <h2 className="text-3xl sm:text-4xl font-bold text-blue-900 mb-4">Free SEO & Digital Presence Audit</h2>
                            <p className="text-gray-700 mb-4 text-base leading-relaxed">
                                Want to see how your garage’s digital footprint compares to your competitors? We’ll provide a detailed SEO and digital presence report—free of charge.
                            </p>
                            <ul className="text-gray-600 text-sm mb-6 list-disc pl-5 space-y-2">
                                <li>On-page SEO & speed performance</li>
                                <li>Keyword rankings & local search visibility</li>
                                <li>Competitor gap analysis</li>
                                <li>Actionable improvements roadmap</li>
                            </ul>
                            <Link to="/bookappointment">
                                <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded shadow transition">
                                    Get My Free Audit Report
                                </button>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Why Choose Us Section */}
                <section className="py-20 bg-blue-900 text-white text-center px-4">
                    <div className="max-w-4xl mx-auto mb-12">
                        <h2 className="text-4xl font-bold mb-4">Why MM AutoTech for Digital Marketing?</h2>
                        <p className="text-white/80 text-base md:text-lg">
                            We’re not just another digital agency—we’re specialists in the automotive service industry. Here’s what sets us apart:
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {[
                            {
                                title: 'Industry Focused',
                                desc: 'We only serve the automotive service industry, so our insights are razor-sharp.'
                            },
                            {
                                title: 'Fully Managed Campaigns',
                                desc: 'We handle everything—from ad copy to analytics—so you can stay under the hood.'
                            },
                            {
                                title: 'Lead-to-Booking Optimization',
                                desc: 'We don’t just get you leads—we help convert them into long-term loyal customers.'
                            }
                        ].map((item, i) => (
                            <div key={i} className="bg-white text-blue-900 p-6 rounded shadow-md hover:shadow-xl transition">
                                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                                <p className="text-sm text-gray-700">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>
                {/* FAQ Section */}
                <section className="max-w-7xl mx-auto px-4">
                    <FAQSection data={DigitalMarketingFAQs} />
                </section>

                {/* CTA Section */}
                <section className="py-16 bg-[#0f172a] text-white px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4">Supercharge Your Garage’s Digital Growth</h2>
                        <p className="text-lg text-white/90 mb-6">
                            Partner with MM AutoTech to attract more leads, automate customer engagement, and gain a measurable return on your marketing investment.
                        </p>
                        <Link to="/bookappointment">
                            <button className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-semibold px-6 py-3 rounded shadow transition-all duration-300">
                                Book My Free Strategy Session
                            </button>
                        </Link>
                    </div>
                </section>
            </div></>
    );
};

export default DigitalMarketing;
